import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Loader2, RefreshCw, Wand2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin tool: scan for legacy free-text `services.category` values that have
 * no `category_id` yet, suggest the best canonical match for each, let admin
 * confirm/override, then bulk-apply.
 */
export function CategoryBackfillCard({ api }) {
  const [scanning, setScanning] = useState(false);
  const [data, setData] = useState(null);
  const [mappings, setMappings] = useState({});  // { legacy: category_id }
  const [applying, setApplying] = useState(false);

  const scan = useCallback(async () => {
    setScanning(true);
    try {
      const { data } = await api.get('/api/admin/category-backfill/suggestions');
      setData(data);
      // Pre-fill mappings with the top suggestion per legacy string.
      const m = {};
      for (const row of data.rows || []) {
        if (row.candidates?.[0]) m[row.legacy_category] = row.candidates[0].id;
      }
      setMappings(m);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  }, [api]);

  useEffect(() => { scan(); }, [scan]);

  const apply = async () => {
    const filtered = Object.fromEntries(
      Object.entries(mappings).filter(([_, v]) => v && v.length > 0)
    );
    if (!Object.keys(filtered).length) {
      toast.warning('Nothing to apply — pick at least one mapping.');
      return;
    }
    if (!window.confirm(`Apply ${Object.keys(filtered).length} mapping(s)? This bulk-updates services in place.`)) return;
    setApplying(true);
    try {
      const { data } = await api.post('/api/admin/category-backfill/apply', { mappings: filtered });
      toast.success(`Backfilled ${data.total_updated} services across ${data.per_mapping.length} mappings`);
      await scan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card className="bg-[#041A3B] border-white/10" data-testid="category-backfill-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-[#00E5FF]" /> Legacy Category Backfill
            </CardTitle>
            <CardDescription className="text-[#A0B4CC]">
              Maps old free-text categories on services → the canonical marketplace categories used by search and filters.
            </CardDescription>
          </div>
          <Button onClick={scan} disabled={scanning} variant="outline"
            className="border-white/20 text-white hover:bg-white/5"
            data-testid="category-backfill-scan">
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Rescan</>}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {scanning && !data ? (
          <div className="text-[#A0B4CC] text-sm flex items-center gap-2 py-3">
            <Loader2 className="w-4 h-4 animate-spin" /> Scanning…
          </div>
        ) : !data || (data.rows || []).length === 0 ? (
          <div className="rounded-lg border border-[#00E676]/30 bg-[#00E676]/5 p-4 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-[#00E676]" />
            <p className="text-[#A0B4CC] text-sm">All services are tied to canonical categories. Nothing to backfill 🎉</p>
          </div>
        ) : (
          <>
            <div className="rounded-lg border border-[#FFA726]/30 bg-[#FFA726]/5 p-3 mb-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-[#FFA726] mt-0.5 flex-shrink-0" />
              <p className="text-[#A0B4CC] text-xs">
                Found <strong className="text-white">{data.total_legacy_strings}</strong> distinct legacy values.
                Pick the right canonical category for each — leave blank to skip a row.
                Bookings, search and filters all key off `category_id`, so this directly improves discoverability.
              </p>
            </div>
            <div className="overflow-x-auto mobile-scroll-wrap max-h-[400px] overflow-y-auto rounded-md border border-white/10">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#000B18] text-[#5C7599] text-left text-xs">
                  <tr><th className="p-2">Legacy value</th><th className="p-2">Services</th><th className="p-2">Map to canonical category</th></tr>
                </thead>
                <tbody className="text-white">
                  {data.rows.map((row) => (
                    <tr key={row.legacy_category} className="border-t border-white/5">
                      <td className="p-2 font-mono text-[#FFA726] truncate max-w-[180px]" title={row.legacy_category}>
                        {row.legacy_category}
                      </td>
                      <td className="p-2 text-[#A0B4CC] whitespace-nowrap">{row.service_count}</td>
                      <td className="p-2">
                        <select
                          value={mappings[row.legacy_category] || ''}
                          onChange={(e) => setMappings((m) => ({ ...m, [row.legacy_category]: e.target.value }))}
                          className="w-full h-8 px-2 rounded bg-[#000B18] border border-white/20 text-white text-xs focus:border-[#00E5FF] focus:outline-none"
                          data-testid={`backfill-map-${row.legacy_category}`}
                        >
                          <option value="">— skip —</option>
                          {row.candidates?.length > 0 && (
                            <optgroup label="Suggested">
                              {row.candidates.map((c) => (
                                <option key={c.id} value={c.id}>{c.label} (score {c.score})</option>
                              ))}
                            </optgroup>
                          )}
                          <optgroup label="All categories">
                            {(data.all_categories || []).map((c) => (
                              <option key={c.id} value={c.id}>{c.label}</option>
                            ))}
                          </optgroup>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end mt-3">
              <Button onClick={apply} disabled={applying}
                className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90"
                data-testid="category-backfill-apply">
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply Mappings'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
